import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { gsap } from 'gsap';

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -30%;
    right: -10%;
    width: 70%;
    height: 120%;
    background: radial-gradient(circle at center, rgba(0, 200, 255, 0.1) 0%, rgba(0, 0, 0, 0) 65%);
    z-index: 1;
    pointer-events: none;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 90%;
  max-width: 1200px;
  margin: 0 auto;
`;

const Eyebrow = styled.p`
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
`;

const Title = styled.h1`
  font-size: clamp(2.6rem, 6.5vw, 5.5rem);
  line-height: 1.08;
  margin-bottom: 2rem;
  max-width: 900px;

  .muted {
    color: ${props => props.theme.colors.gray};
  }
`;

const Subtitle = styled.p`
  font-size: clamp(1.1rem, 2vw, 1.35rem);
  color: ${props => props.theme.colors.gray};
  max-width: 640px;
  margin-bottom: 2.5rem;
  line-height: 1.7;
`;

const CertBadge = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.6rem 1.1rem 0.6rem 0.7rem;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: 999px;
  background: ${props => props.theme.colors.dark};
  color: ${props => props.theme.colors.text};
  font-size: 0.95rem;
  font-weight: 500;
  margin-bottom: 3rem;
  transition: border-color 0.3s ease, transform 0.3s ease;

  img {
    width: 32px;
    height: 32px;
    border-radius: 50%;
  }

  span.issuer {
    color: ${props => props.theme.colors.gray};
  }

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const PrimaryButton = styled.a`
  display: inline-block;
  padding: 1rem 2.2rem;
  border-radius: 8px;
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.background};
  font-weight: 600;
  font-size: 1rem;
  transition: background 0.3s ease, transform 0.3s ease;

  &:hover {
    background: ${props => props.theme.colors.primaryHover};
    transform: translateY(-2px);
  }
`;

const SecondaryButton = styled.a`
  display: inline-block;
  padding: 1rem 2.2rem;
  border-radius: 8px;
  border: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.text};
  font-weight: 600;
  font-size: 1rem;
  transition: border-color 0.3s ease, transform 0.3s ease;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
  }
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  color: ${props => props.theme.colors.gray};
  font-size: 0.85rem;

  .line {
    width: 1px;
    height: 50px;
    background-color: ${props => props.theme.colors.primary};
    animation: scrollLine 2s ease-in-out infinite;
  }

  @keyframes scrollLine {
    0% { transform: scaleY(0); transform-origin: top; }
    50% { transform: scaleY(1); transform-origin: top; }
    50.1% { transform: scaleY(1); transform-origin: bottom; }
    100% { transform: scaleY(0); transform-origin: bottom; }
  }
`;

const CREDLY_URL = 'https://www.credly.com/badges/c0dece68-3945-4488-bd8a-fc84b8554c96';
const CREDLY_BADGE_IMG = 'https://images.credly.com/size/340x340/images/f2040db3-3904-4240-8966-e87b1510bea0/blob';

const Hero = () => {
  const contentRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(contentRef.current.children, {
        y: 40,
        opacity: 0,
        duration: 0.9,
        stagger: 0.12,
        ease: 'power3.out',
      });
    }, contentRef);
    return () => ctx.revert();
  }, []);

  return (
    <HeroSection id="home">
      <HeroContent ref={contentRef}>
        <Eyebrow>Hayk Avdalyan — Software Engineering Manager &amp; AI Consultant</Eyebrow>
        <Title>
          I help businesses put AI to work.
          <span className="muted"> Strategy, tools, and agents — integrated hands-on.</span>
        </Title>
        <Subtitle>
          From identifying where AI creates real value to building and integrating AI agents
          into your operations, I bring 12+ years of engineering and leadership experience
          to make AI adoption practical, not theoretical.
        </Subtitle>
        <CertBadge href={CREDLY_URL} target="_blank" rel="noopener noreferrer">
          <img src={CREDLY_BADGE_IMG} alt="Claude Certified Architect badge" />
          Claude Certified Architect
          <span className="issuer">· Anthropic</span>
        </CertBadge>
        <Actions>
          <PrimaryButton href="#contact">Work with me</PrimaryButton>
          <SecondaryButton href="#services">Explore services</SecondaryButton>
        </Actions>
      </HeroContent>
      <ScrollIndicator>
        <span>Scroll Down</span>
        <div className="line" />
      </ScrollIndicator>
    </HeroSection>
  );
};

export default Hero;
